import {
    LayoutDashboard, ShieldCheck, ListTree, Users, Building2, Tag, Wallet, Receipt,
    History, MessageSquareWarning, UserCircle, MapPin, Stethoscope, BadgeCheck,
    ScanLine, Home, CreditCard, ClipboardList, UserPlus, BarChart3, ChevronRight, UserCog,
    Tags,
} from "lucide-react";
import { ROLES } from "@/lib/roles";

/**
 * Sidebar groups by role.
 *
 * Schema:
 *   group  = { label, items: NavItem[], actions?: NavAction[], collapsible?: boolean }
 *   item   = { to?, labelKey, Icon, testId?, badge?: {kind, source}, items?: NavItem[], end? }
 *
 * - `items` (formerly `children`) supports unbounded depth.
 * - `badge.source` keys are resolved via the useNavCounts() hook.
 * - Older configs that still use `children` are backwards-compatible — Sidebar.jsx
 *   reads either `items` or `children`.
 */
export function navForRole(role) {
    switch (role) {
        case ROLES.SUPER_ADMIN:
            return [
                {
                    label: "groups.operations",
                    items: [
                        { to: "/admin/dashboard", labelKey: "nav.dashboard",  Icon: LayoutDashboard, testId: "nav-admin-dashboard" },
                        { to: "/admin/kyc",       labelKey: "nav.kyc",        Icon: ShieldCheck,     testId: "nav-admin-kyc",
                          badge: { kind: "count", source: "kyc.pending" } },
                        { to: "/admin/categories",labelKey: "nav.categories", Icon: ListTree,        testId: "nav-admin-categories" },
                        { to: "/admin/members",   labelKey: "nav.members",    Icon: Users,           testId: "nav-admin-members",
                          badge: { kind: "count", source: "members.pending_review" } },
                        { to: "/admin/providers", labelKey: "nav.providers",  Icon: Building2,       testId: "nav-admin-providers" },
                        {
                            to: "/admin/offers",
                            labelKey: "nav.offers",
                            Icon: Tag,
                            testId: "nav-admin-offers",
                            items: [
                                { to: "/admin/offers", labelKey: "nav.offersAll", Icon: ChevronRight, testId: "nav-admin-offers-all", end: true },
                                { to: "/admin/offers/pending", labelKey: "nav.offersPending", Icon: ShieldCheck, testId: "nav-admin-offers-pending",
                                  badge: { kind: "count", source: "offers.pending" } },
                            ],
                        },
                        { to: "/admin/disputes",  labelKey: "nav.disputes",   Icon: MessageSquareWarning, testId: "nav-admin-disputes",
                          badge: { kind: "count", source: "disputes.open" } },
                    ],
                },
                {
                    label: "groups.administration",
                    items: [
                        { to: "/admin/users",          labelKey: "nav.users",         Icon: UserCog, testId: "nav-admin-users" },
                        { to: "/admin/provider-types", labelKey: "nav.providerTypes", Icon: Tags,    testId: "nav-admin-provider-types" },
                    ],
                },
                {
                    label: "groups.finance",
                    items: [
                        { to: "/admin/wallets",     labelKey: "nav.wallets",     Icon: Wallet,  testId: "nav-admin-wallets" },
                        { to: "/admin/commissions", labelKey: "nav.commissions", Icon: Receipt, testId: "nav-admin-commissions" },
                        { to: "/admin/audit",       labelKey: "nav.audit",       Icon: History, testId: "nav-admin-audit" },
                    ],
                },
            ];
        case ROLES.OPS_ADMIN:
            return [{
                label: "groups.operations",
                items: [
                    { to: "/admin/dashboard", labelKey: "nav.dashboard", Icon: LayoutDashboard, testId: "nav-admin-dashboard" },
                    { to: "/admin/kyc",       labelKey: "nav.kyc",       Icon: ShieldCheck,     testId: "nav-admin-kyc",
                      badge: { kind: "count", source: "kyc.pending" } },
                    { to: "/admin/members",   labelKey: "nav.members",   Icon: Users,           testId: "nav-admin-members",
                      badge: { kind: "count", source: "members.pending_review" } },
                    { to: "/admin/providers", labelKey: "nav.providers", Icon: Building2,       testId: "nav-admin-providers" },
                    {
                        to: "/admin/offers",
                        labelKey: "nav.offers",
                        Icon: Tag,
                        testId: "nav-admin-offers",
                        items: [
                            { to: "/admin/offers", labelKey: "nav.offersAll", Icon: ChevronRight, testId: "nav-admin-offers-all", end: true },
                            { to: "/admin/offers/pending", labelKey: "nav.offersPending", Icon: ShieldCheck, testId: "nav-admin-offers-pending",
                              badge: { kind: "count", source: "offers.pending" } },
                        ],
                    },
                    { to: "/admin/disputes",  labelKey: "nav.disputes",  Icon: MessageSquareWarning, testId: "nav-admin-disputes",
                      badge: { kind: "count", source: "disputes.open" } },
                ],
            }];
        case ROLES.FINANCE_ADMIN:
            return [{
                label: "groups.finance",
                items: [
                    { to: "/admin/dashboard",   labelKey: "nav.dashboard",   Icon: LayoutDashboard, testId: "nav-admin-dashboard" },
                    { to: "/admin/wallets",     labelKey: "nav.wallets",     Icon: Wallet,          testId: "nav-admin-wallets" },
                    { to: "/admin/commissions", labelKey: "nav.commissions", Icon: Receipt,         testId: "nav-admin-commissions" },
                ],
            }];
        case ROLES.SALES_AGENT:
            return [{
                label: "groups.field",
                items: [
                    { to: "/kiosk/sales",     labelKey: "nav.mySales",        Icon: BarChart3, testId: "nav-kiosk-sales" },
                    { to: "/kiosk/register",  labelKey: "nav.kioskRegister",  Icon: UserPlus,  testId: "nav-kiosk-register" },
                    { to: "/kiosk/members",   labelKey: "nav.kioskMembers",   Icon: Users,     testId: "nav-kiosk-members" },
                    { to: "/kiosk/providers", labelKey: "nav.kioskProviders", Icon: Building2, testId: "nav-kiosk-providers" },
                    { to: "/kiosk/wallet",    labelKey: "nav.myWallet",       Icon: Wallet,    testId: "nav-kiosk-wallet" },
                ],
            }];
        case ROLES.PROVIDER_ADMIN:
            return [{
                label: "groups.operations",
                items: [
                    { to: "/provider/dashboard", labelKey: "nav.dashboard", Icon: LayoutDashboard, testId: "nav-provider-dashboard" },
                    { to: "/provider/profile",   labelKey: "nav.profile",   Icon: UserCircle,      testId: "nav-provider-profile" },
                    { to: "/provider/kyc",       labelKey: "nav.kyc",       Icon: ShieldCheck,     testId: "nav-provider-kyc" },
                    { to: "/provider/locations", labelKey: "nav.locations", Icon: MapPin,          testId: "nav-provider-locations" },
                    { to: "/provider/staff",     labelKey: "nav.staff",     Icon: Stethoscope,     testId: "nav-provider-staff" },
                    { to: "/provider/offers",    labelKey: "nav.offers",    Icon: Tag,             testId: "nav-provider-offers" },
                    { to: "/provider/verify",    labelKey: "nav.verify",    Icon: ScanLine,        testId: "nav-provider-verify" },
                    { to: "/provider/visits",    labelKey: "nav.visits",    Icon: ClipboardList,   testId: "nav-provider-visits" },
                ],
            }];
        case ROLES.PROVIDER_STAFF:
            return [{
                label: "groups.operations",
                items: [
                    { to: "/provider/verify", labelKey: "nav.verify", Icon: ScanLine,      testId: "nav-provider-verify" },
                    { to: "/provider/visits", labelKey: "nav.visits", Icon: ClipboardList, testId: "nav-provider-visits" },
                ],
            }];
        case ROLES.PROVIDER_EXPERT:
            return [{
                label: "groups.myAccount",
                items: [
                    { to: "/provider/expert/profile", labelKey: "nav.profile",   Icon: UserCircle,    testId: "nav-expert-profile" },
                    { to: "/provider/expert/offers",  labelKey: "nav.offers",    Icon: Tag,           testId: "nav-expert-offers" },
                    { to: "/provider/expert/visits",  labelKey: "nav.myVisits",  Icon: ClipboardList, testId: "nav-expert-visits" },
                ],
            }];
        case ROLES.MEMBER:
            return [{
                label: "groups.myAccount",
                items: [
                    { to: "/member/home",   labelKey: "nav.home",     Icon: Home,        testId: "nav-member-home" },
                    { to: "/member/card",   labelKey: "nav.myCard",   Icon: CreditCard,  testId: "nav-member-card" },
                    { to: "/member/visits", labelKey: "nav.myVisits", Icon: BadgeCheck,  testId: "nav-member-visits" },
                ],
            }];
        default:
            return [];
    }
}

/**
 * Walk all nav items (recursively) for a role and return a flat list with
 * { to, labelKey, Icon, testId } for each leaf item. Used by:
 *   - the sidebar search input (filter by label)
 *   - the Pinned section (resolve pinned `to` paths back to renderable items)
 */
export function flattenNav(groups) {
    const out = [];
    const walk = (items) => {
        for (const it of items || []) {
            if (it.to) out.push({ to: it.to, labelKey: it.labelKey, Icon: it.Icon, testId: it.testId, badge: it.badge });
            if (it.items?.length) walk(it.items);
            else if (it.children?.length) walk(it.children); // legacy
        }
    };
    for (const g of groups || []) walk(g.items);
    return out;
}
